import type { ServerAction, ServerActionResponse } from "@cloudserver/contracts";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../../api/client.js";
import { useServerAction } from "../../api/servers.js";
import { problemText } from "../../ui/feedback.js";
import { useToast } from "../../ui/toast.js";
import type { ServerTabProps } from "./types.js";

/**
 * Starts a server action from a tab: a toast says it started, the page
 * follows the operation to its end, errors are told in plain language.
 */
export function useStartAction(props: ServerTabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const mutation = useServerAction(props.projectId);
  const start = (
    action: ServerAction,
    body?: object,
    onSuccess?: (result: ServerActionResponse) => void,
  ) =>
    mutation.mutate(
      { serverId: props.server.id, action, ...(body === undefined ? {} : { body }) },
      {
        onSuccess: (result) => {
          toast({
            tone: "info",
            title: t("servers.started", {
              what: t(`servers.kinds.${action.replaceAll("-", "_")}`),
              name: props.server.name,
            }),
          });
          props.watch(result.operation.id);
          onSuccess?.(result);
        },
        onError: (error) =>
          toast({
            tone: "danger",
            title:
              error instanceof ApiProblem && error.problem
                ? problemText(t, error.problem)
                : t("common.unexpected"),
          }),
      },
    );
  return { start, pending: mutation.isPending, error: mutation.error };
}
